/**
 * product-coupons.js
 *
 * Responsibility: Handles 1-click clipboard copy interactions for product page coupon codes.
 *
 * AGENTS.md compliance:
 * - No global variables. Enclosed in IIFE.
 * - All DOM queries use data-* attributes only. Centralized in getCouponElements().
 * - Functions named as verbs (init, handle, copy, set).
 * - Booleans prefixed with is/has/can.
 * - Defensive try/catch on clipboard operations with fallback.
 * - No timing hacks or memory leaks.
 */

(function initProductCoupons() {
  const STATE_CLASS_COPIED = 'is-copied';
  const LABEL_DEFAULT = 'COPY';
  const LABEL_COPIED = 'COPIED!';
  const RESET_TIMEOUT_MS = 2000;

  /**
   * Centralized DOM queries for coupon components on the page.
   * @returns {NodeListOf<HTMLElement>}
   */
  function getCouponContainers() {
    try {
      return document.querySelectorAll('[data-product-coupons]');
    } catch (e) {
      return [];
    }
  }

  /**
   * Copies string to clipboard with defensive fallbacks.
   * @param {string} textToCopy
   * @returns {Promise<boolean>}
   */
  async function copyTextToClipboard(textToCopy) {
    if (!textToCopy) return false;

    // Modern Clipboard API
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(textToCopy);
        return true;
      } catch (e) {
        // Fallback to execCommand below
      }
    }

    // Fallback for older webviews / insecure contexts
    try {
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      textarea.setAttribute('readonly', 'true');
      document.body.appendChild(textarea);
      textarea.select();
      const isSuccessful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return isSuccessful;
    } catch (e) {
      return false;
    }
  }

  /**
   * Applies temporary 'COPIED!' visual state to the clicked button.
   * @param {HTMLButtonElement} buttonElement
   */
  function setCopiedState(buttonElement) {
    if (!buttonElement) return;

    const labelElement = buttonElement.querySelector('[data-btn-label]');
    buttonElement.classList.add(STATE_CLASS_COPIED);
    if (labelElement) {
      labelElement.textContent = LABEL_COPIED;
    }

    // Clear previous timer if exists
    if (buttonElement._resetTimerId) {
      clearTimeout(buttonElement._resetTimerId);
    }

    buttonElement._resetTimerId = setTimeout(function () {
      buttonElement.classList.remove(STATE_CLASS_COPIED);
      if (labelElement) {
        labelElement.textContent = LABEL_DEFAULT;
      }
      buttonElement._resetTimerId = null;
    }, RESET_TIMEOUT_MS);
  }

  /**
   * Event handler for copy button click.
   * @param {MouseEvent} event
   */
  async function handleCopyButtonClick(event) {
    const target = event.currentTarget;
    if (!target) return;

    const couponCode = target.getAttribute('data-target-code');
    if (!couponCode) return;

    const isCopied = await copyTextToClipboard(couponCode);
    if (isCopied) {
      setCopiedState(target);
    }
  }

  /**
   * Binds click events to all copy buttons inside coupon containers.
   */
  function bindCopyButtons() {
    const containers = getCouponContainers();
    if (!containers || containers.length === 0) return;

    containers.forEach(function (container) {
      const copyButtons = container.querySelectorAll('[data-copy-button]');
      copyButtons.forEach(function (btn) {
        // Prevent duplicate listeners
        if (btn.dataset.hasCopyListener === 'true') return;
        btn.dataset.hasCopyListener = 'true';
        btn.addEventListener('click', handleCopyButtonClick);
      });
    });
  }

  // ─── Initialization ─────────────────────────────────────────────────────────

  function init() {
    bindCopyButtons();

    // Re-bind if DOM morphs or section renders
    document.addEventListener('shopify:section:load', bindCopyButtons);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
